<template>
  <div class="common-title-page">
    <div class="title-wrapper">
      <pods-input ref="podsInput"></pods-input>
      <span class="title">Data View</span>
    </div>
    <div class="mb-20 mt-20 v-h-between">
      <!-- Column Selection Control -->
      <div>
        <el-select
          v-model="selectedColumns"
          multiple
          collapse-tags
          collapse-tags-tooltip
          placeholder="Select columns to display"
          size="small"
          style="width: 220px"
        >
          <el-option
            v-for="column in availableColumns"
            :key="column"
            :label="getColumnLabel(column)"
            :value="column"
          >
          </el-option>
        </el-select>
      </div>

      <UtcOffsets />
    </div>

    <el-table :data="tableData" border style="width: 100%" size="small">
      <el-table-column
        v-for="column in selectedColumns"
        :key="column"
        :prop="column"
        :label="getColumnLabel(column)"
      >
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import PodsInput from '@/components/PodsInput'
import UtcOffsets from '@/components/UtcOffsets'

// Column label mappings for better display
const COLUMN_LABELS = {
  tickCounter: 'Tickcounter',
  connectionStatus: 'Connection Status',
  sensor1Value: 'Sensor 1 / Aggregate',
  sensor2Value: 'Sensor 2 / Refined',
  ax: 'Ax',
  ay: 'Ay',
  az: 'Az',
  lastConnectedTickCounter: 'Last Pad Connection',
  batteryVoltage: 'Battery Voltage',
  temperature: 'Temperature',
  fallEvent: 'Fall Event',
  lastFallTickCounter: 'Fall TickCounter',
  firmwareVersion: 'FW Version',
  timeStampWithUtc: 'Time',
  podMac: 'Pod MAC',
  podId: 'Pod ID',
  podStatus: 'Pod Status'
}

// Default columns to display
const DEFAULT_COLUMNS = [
  'tickCounter',
  'connectionStatus',
  'sensor1Value',
  'sensor2Value',
  'ax',
  'ay',
  'az',
  'lastConnectedTickCounter',
  'batteryVoltage',
  'temperature',
  'fallEvent',
  'lastFallTickCounter',
  'firmwareVersion',
  'timeStampWithUtc'
]

export default {
  props: {
    podData: {
      require: true,
      type: Array
    },
    screenWidth: {
      require: true,
      type: Number
    }
  },
  data() {
    return {
      selectedColumns: [...DEFAULT_COLUMNS],
      availableColumns: [],
      columnLabels: COLUMN_LABELS
    }
  },
  computed: {
    tableData() {
      let data = JSON.parse(JSON.stringify(this.podData))
      return data.reverse()
    }
  },
  watch: {
    // Watch podData to update available columns dynamically
    podData: {
      deep: true,
      handler() {
        this.updateAvailableColumns()
      }
    }
  },
  mounted() {
    // Initialize available columns from current podData
    this.updateAvailableColumns()
  },
  components: {
    PodsInput,
    UtcOffsets
  },
  methods: {
    /**
     * Update available columns based on data received from SignalR
     */
    updateAvailableColumns() {
      if (!this.podData || this.podData.length === 0) {
        this.availableColumns = [...DEFAULT_COLUMNS]
        return
      }

      // Get all keys from the first data object
      const firstDataKeys = Object.keys(this.podData[0])

      // Filter out timestamp-related columns
      const excludedColumns = ['timeStamp', 'timestamp']
      const filteredKeys = firstDataKeys.filter(
        key => !excludedColumns.includes(key.toLowerCase())
      )

      // Merge with default columns and remove duplicates
      const uniqueColumns = new Set([...DEFAULT_COLUMNS, ...filteredKeys])
      this.availableColumns = Array.from(uniqueColumns)

      // Ensure currently selected columns are valid
      this.selectedColumns = this.selectedColumns.filter(col =>
        this.availableColumns.includes(col)
      )

      // If no columns are selected, use defaults
      if (this.selectedColumns.length === 0) {
        this.selectedColumns = DEFAULT_COLUMNS.filter(col =>
          this.availableColumns.includes(col)
        )
      }
    },

    /**
     * Get display label for a column
     */
    getColumnLabel(column) {
      return COLUMN_LABELS[column] || column
    }
  }
}
</script>
